/**
 * WhatsAppPhone: celular com a conversa vista pelo contato.
 *
 * Mostra o template como o cliente o recebe (balão à esquerda), com o texto
 * inteiro e rolagem interna. Os botões são clicáveis só para simular:
 * resposta rápida entra na conversa, link e ligação mostram o destino.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BadgeCheck, Copy, ExternalLink, Mic, Phone, SendHorizontal, Smile, Video } from "lucide-react";
import { CHAT_THEMES, CHAT_BACKGROUND } from "./chatTheme";
import { BUBBLE_SHADOW, MessageBubble, TAIL_OUT, formatTime } from "./MessageBubble";

export { CHAT_THEMES };

/** Descreve o que o toque em um botão faz, para o aviso da simulação. */
function describeAction(button) {
  switch (button.type) {
    case "URL":
      return { icon: ExternalLink, title: "Abre o link no navegador", detail: button.url };
    case "PHONE_NUMBER":
      return { icon: Phone, title: "Abre o discador", detail: button.phone_number };
    case "COPY_CODE":
      return { icon: Copy, title: "Copia o código para a área de transferência", detail: button.example };
    case "FLOW":
      return { icon: ExternalLink, title: "Abre o formulário do WhatsApp", detail: null };
    default:
      return { icon: ExternalLink, title: "Executa a ação do botão", detail: null };
  }
}

/** Folha de opções de uma mensagem de lista, aberta sobre a conversa como o WhatsApp a mostra. */
function ListSheet({ list, onClose }) {
  const title = list.button || "Ver opções";
  return (
    <div className="absolute inset-0 z-10 flex flex-col justify-end">
      <button type="button" aria-label="Fechar lista" onClick={onClose} className="absolute inset-0 bg-black/45" />
      <div
        role="dialog"
        aria-label={title}
        className="relative max-h-[70%] overflow-y-auto rounded-t-2xl pb-5 pt-2 [scrollbar-width:thin]"
        style={{ background: "var(--wa-head)", color: "var(--wa-head-fg)" }}
      >
        <div className="mx-auto mb-2 h-1 w-9 rounded-full opacity-40" style={{ background: "var(--wa-sub)" }} />
        <p className="px-4 pb-1 text-[13px] font-semibold">{title}</p>
        {(list.sections || []).map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.title && (
              <p data-list-section-title className="px-4 pt-2 text-[11px] font-semibold text-[#00a884]">
                {section.title}
              </p>
            )}
            {(section.rows || []).map((row, rowIndex) => (
              <div key={rowIndex} className="flex items-center gap-3 px-4 py-2 text-[12.5px]">
                <span className="h-4 w-4 shrink-0 rounded-full border-[1.5px]" style={{ borderColor: "var(--wa-sub)" }} />
                <div className="min-w-0">
                  <div>{row.title}</div>
                  {row.description && (
                    <small className="block text-[11px]" style={{ color: "var(--wa-sub)" }}>
                      {row.description}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.template - Template no formato da Meta (components[])
 * @param {Record<string,string>} [props.values] - Valores das variáveis
 * @param {'example'|'model'} [props.mode]
 * @param {'light'|'dark'} [props.theme]
 * @param {string} [props.businessName]
 * @param {string} [props.width] - Largura CSS do aparelho
 * @param {{from: 'contact'|'agent', text: string}[]} [props.conversation] - Conversa de exemplo, em
 *   ordem, no lugar do template: o contato à direita, a empresa à esquerda (prévia dos agentes).
 * @param {{value: string, onChange: (text: string) => void, onSend: () => void, disabled?: boolean, placeholder?: string}} [props.composer]
 *   Torna o campo "Mensagem" do aparelho um campo de verdade (conversa de teste com o agente).
 */
export function WhatsAppPhone({
  template,
  values,
  mode = "example",
  theme = "light",
  businessName = "Empresa",
  width = "300px",
  conversation = null,
  composer = null,
}) {
  const listComp = template?.components?.find((c) => c.type === "LIST");
  const now = useMemo(() => new Date(), []);
  const time = formatTime(now);

  const [replies, setReplies] = useState([]);
  const [notice, setNotice] = useState(null);
  const [listOpen, setListOpen] = useState(listComp?.open !== false);
  const chatRef = useRef(null);
  const noticeTimer = useRef(null);

  // Trocar de template recomeça a conversa
  useEffect(() => {
    setReplies([]);
    setNotice(null);
    setListOpen(template?.components?.find((c) => c.type === "LIST")?.open !== false);
    if (chatRef.current) chatRef.current.scrollTop = 0;
  }, [template]);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // Respostas rápidas e a conversa de teste (com campo) acompanham a última mensagem.
  useEffect(() => {
    const el = chatRef.current;
    if (el && (replies.length || composer)) el.scrollTop = el.scrollHeight;
  }, [replies, composer, conversation]);

  const handleButton = (button, index) => {
    if (!button.type || button.type === "QUICK_REPLY") {
      setReplies((prev) => [...prev, { id: `${index}-${prev.length}`, text: button.text }]);
      return;
    }
    setNotice(describeAction(button));
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3200);
  };

  const ctx = { values, mode };

  return (
    <div
      data-testid="whatsapp-phone"
      className="relative shrink-0 rounded-[46px] bg-[#0a0f12] p-[9px] shadow-[0_18px_40px_rgba(0,0,0,0.28),inset_0_0_0_1.5px_#2a3338]"
      style={{ width, aspectRatio: "9 / 19", ...CHAT_THEMES[theme] }}
    >
      <div
        className="relative flex h-full w-full flex-col overflow-hidden rounded-[38px]"
        style={{ background: "var(--wa-bg)", color: "var(--wa-in-fg)" }}
      >
        <div className="absolute left-1/2 top-[9px] z-10 h-6 w-[88px] -translate-x-1/2 rounded-full bg-black" />

        {/* Barra de status */}
        <div
          className="flex h-[42px] shrink-0 items-center justify-between px-6 pt-3 text-xs font-semibold"
          style={{ background: "var(--wa-head)", color: "var(--wa-bar)" }}
        >
          <span>{time}</span>
          <span className="flex items-center gap-1.5" aria-hidden="true">
            <svg width="16" height="11" viewBox="0 0 17 11" fill="currentColor">
              <rect x="0" y="7" width="3" height="4" rx=".7" />
              <rect x="4.5" y="5" width="3" height="6" rx=".7" />
              <rect x="9" y="2.5" width="3" height="8.5" rx=".7" />
              <rect x="13.5" y="0" width="3" height="11" rx=".7" />
            </svg>
            <svg width="22" height="11" viewBox="0 0 24 12" fill="none" stroke="currentColor">
              <rect x=".5" y=".5" width="20" height="11" rx="3" />
              <rect x="2" y="2" width="14" height="8" rx="1.6" fill="currentColor" stroke="none" />
              <path d="M22 4v4" strokeLinecap="round" />
            </svg>
          </span>
        </div>

        {/* Cabeçalho do chat */}
        <div
          className="flex shrink-0 items-center gap-2 pb-2 pl-1.5 pr-2.5 pt-1.5"
          style={{ background: "var(--wa-head)", color: "var(--wa-head-fg)" }}
        >
          <ArrowLeft className="h-5 w-5 shrink-0" />
          <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#00a884] text-[13px] font-bold text-white">
            {(businessName || "E").trim().charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex items-center gap-1 text-[13.5px] font-semibold">
              <span className="truncate">{businessName}</span>
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 fill-[#25d366] text-white" />
            </div>
            <div className="text-[11px]" style={{ color: "var(--wa-sub)" }}>conta comercial</div>
          </div>
          <Video className="h-[19px] w-[19px] shrink-0" />
          <Phone className="ml-1.5 h-[18px] w-[18px] shrink-0" />
        </div>

        {/* Conversa */}
        <div
          ref={chatRef}
          tabIndex={0}
          aria-label="Conversa de exemplo"
          data-testid="whatsapp-chat"
          className="flex min-h-0 flex-1 flex-col gap-[3px] overflow-y-auto px-2.5 pb-3.5 pt-2.5 [scrollbar-width:thin]"
          style={CHAT_BACKGROUND}
        >
          <span
            className="mb-1.5 mt-0.5 self-center rounded-[7px] px-[9px] py-[3px] text-[10.5px] uppercase tracking-wide"
            style={{ background: "var(--wa-chip)", color: "var(--wa-sub)" }}
          >
            Hoje
          </span>

          {Array.isArray(conversation) &&
            conversation.map((message, index) => (
              <MessageBubble
                key={`${message.from}-${index}`}
                template={{ components: [{ type: "BODY", text: message.text }] }}
                time={time}
                direction={message.from === "contact" ? "out" : "in"}
              />
            ))}

          {template && <MessageBubble
            template={template}
            ctx={ctx}
            time={time}
            onButton={handleButton}
            listOpen={listOpen}
            onToggleList={() => setListOpen((open) => !open)}
            onCallPermission={() => setNotice({ icon: Phone, title: "Pede ao contato permissão para ligar", detail: null })}
          />}

          {replies.map((reply) => (
            <div key={reply.id} className="relative max-w-[88%] self-end rounded-lg rounded-tr-none p-1 text-[13px] leading-[1.34]" style={{ background: "var(--wa-out)", color: "var(--wa-in-fg)", boxShadow: BUBBLE_SHADOW }}>
              <span aria-hidden="true" className="absolute -right-[7px] top-0 h-3 w-2" style={{ background: "var(--wa-out)", clipPath: TAIL_OUT }} />
              <div className="break-words px-[5px] pt-0.5">{reply.text}</div>
              <div className="flex justify-end gap-1 px-[5px] pb-px text-[10.5px]" style={{ color: "var(--wa-meta)" }}>
                {time}
                <svg width="15" height="11" viewBox="0 0 16 11" fill="none" stroke="#53bdeb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-label="Lida">
                  <path d="M1 6l3.2 3.2L10.5 1.5M6 8.5l.7.7L13 1.5" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {composer ? (
          <form
            className="flex shrink-0 items-center gap-1.5 px-2 pb-1 pt-1.5"
            style={{ background: "var(--wa-bg)" }}
            onSubmit={(e) => {
              e.preventDefault();
              if (!composer.disabled && composer.value.trim()) composer.onSend();
            }}
          >
            <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full px-2.5 text-[13px]" style={{ background: "var(--wa-input)", color: "var(--wa-sub)" }}>
              <Smile className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              <input
                value={composer.value}
                onChange={(e) => composer.onChange(e.target.value)}
                placeholder={composer.placeholder || "Mensagem"}
                aria-label="Mensagem para o agente"
                autoFocus
                className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-[var(--wa-sub)]"
                style={{ color: "var(--wa-in-fg)" }}
              />
            </div>
            <button
              type="submit"
              aria-label="Enviar"
              disabled={composer.disabled || !composer.value.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00a884] disabled:opacity-60"
            >
              {composer.value.trim() ? <SendHorizontal className="h-[17px] w-[17px] text-white" /> : <Mic className="h-[17px] w-[17px] text-white" />}
            </button>
          </form>
        ) : (
        /* Campo de mensagem (decorativo) */
        <div className="flex shrink-0 items-center gap-1.5 px-2 pb-1 pt-1.5" style={{ background: "var(--wa-bg)" }} aria-hidden="true">
          <div className="flex h-9 flex-1 items-center gap-2 rounded-full px-2.5 text-[13px]" style={{ background: "var(--wa-input)", color: "var(--wa-sub)" }}>
            <Smile className="h-[18px] w-[18px]" />
            Mensagem
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#00a884]">
            <Mic className="h-[17px] w-[17px] text-white" />
          </div>
        </div>
        )}
        <div className="flex h-4 shrink-0 items-center justify-center" style={{ background: "var(--wa-bg)" }} aria-hidden="true">
          <i className="h-1 w-[90px] rounded-full opacity-55" style={{ background: "var(--wa-bar)" }} />
        </div>

        {listComp && listOpen && <ListSheet list={listComp} onClose={() => setListOpen(false)} />}

        {notice && (
          <div role="status" className="absolute inset-x-3 bottom-[70px] z-20 flex items-start gap-2 rounded-[10px] bg-[#111b21] px-3 py-2.5 text-xs text-[#e9edef] shadow-lg">
            <notice.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="min-w-0">
              <div>{notice.title}</div>
              {notice.detail && <code className="break-all font-mono text-[11px] text-[#9fe0f3]">{notice.detail}</code>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
